import React, { useEffect, useRef, useState } from 'react';

const ClientAdSense = ({
    slot,
    client = 'ca-pub-7364280401206234',
    format = 'auto',
    fullWidthResponsive = true,
    style,
    className = '',
    containerClassName = '',
    minHeight = 100,
    failTimeoutMs = 8000,
    lazyLoad = false,
    rootMargin = '300px',
    label = 'Advertisement',
}) => {
    const wrapperRef = useRef(null);
    const insRef = useRef(null);
    const pushed = useRef(false);
    const [inView, setInView] = useState(!lazyLoad);
    // 'loading' | 'filled' | 'failed'
    const [status, setStatus] = useState('loading');

    // IntersectionObserver — only mark inView when wrapper scrolls into viewport
    useEffect(() => {
        if (!lazyLoad || inView) return;
        const target = wrapperRef.current;
        if (!target || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin, threshold: 0 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [lazyLoad, inView, rootMargin]);

    // Push only AFTER the element has come into view
    useEffect(() => {
        if (!inView || pushed.current) return;

        let pushInterval;
        let pushTimeout;
        let statusInterval;
        let statusTimeout;

        const watchStatus = () => {
            statusInterval = setInterval(() => {
                const el = insRef.current;
                if (!el) return;
                const adStatus = el.getAttribute('data-ad-status');
                if (adStatus === 'filled') {
                    setStatus('filled');
                    clearInterval(statusInterval);
                    clearTimeout(statusTimeout);
                } else if (adStatus === 'unfilled') {
                    setStatus('failed');
                    clearInterval(statusInterval);
                    clearTimeout(statusTimeout);
                }
            }, 300);
            statusTimeout = setTimeout(() => {
                clearInterval(statusInterval);
                setStatus((curr) => (curr === 'loading' ? 'failed' : curr));
            }, failTimeoutMs);
        };

        const tryPush = () => {
            if (!insRef.current) return false;
            if (insRef.current.getAttribute('data-adsbygoogle-status')) {
                pushed.current = true;
                return true;
            }
            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
                pushed.current = true;
                return true;
            } catch (err) {
                return false;
            }
        };

        if (tryPush()) {
            watchStatus();
        } else {
            pushInterval = setInterval(() => {
                if (tryPush()) {
                    clearInterval(pushInterval);
                    clearTimeout(pushTimeout);
                    watchStatus();
                }
            }, 400);
            pushTimeout = setTimeout(() => {
                clearInterval(pushInterval);
                setStatus('failed');
            }, 10000);
        }

        return () => {
            clearInterval(pushInterval);
            clearTimeout(pushTimeout);
            clearInterval(statusInterval);
            clearTimeout(statusTimeout);
        };
    }, [inView, failTimeoutMs]);

    const isDev =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.startsWith('192.168.'));

    // Lazy-load placeholder — keep wrapper in DOM so the observer has a target,
    // but don't render the <ins> until the user scrolls to this area.
    if (lazyLoad && !inView) {
        return (
            <div className={containerClassName}>
                <div
                    ref={wrapperRef}
                    className='w-full'
                    style={{ minHeight }}
                />
            </div>
        );
    }

    // Ad unfilled / failed / timeout in PRODUCTION — collapse entirely.
    // Dev mode: show plain text marker so the slot is visible during development.
    if (status === 'failed') {
        if (!isDev) return null;
        return (
            <div className={containerClassName}>
                <div
                    className='w-full flex items-center justify-center'
                    style={{ minHeight }}
                >
                    <span
                        className='text-[11px]'
                        style={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                        Ad slot {slot} (dev — AdSense doesn't serve on localhost)
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={containerClassName}>
            {/* Policy-compliant "Advertisement" label above the slot —
                clearly identifies sponsored content per AdSense guidelines. */}
            <div
                className='w-full text-center mb-1.5'
                style={{
                    fontSize: '10px',
                    letterSpacing: '0.3em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.35)',
                }}
            >
                {label}
            </div>

            <div
                ref={wrapperRef}
                className={`relative w-full ${className}`}
                style={{ minHeight }}
            >
                {/* Shimmer sits BEHIND the <ins> via z-index.
                    When Google serves an ad, its iframe naturally sits on top —
                    nothing overlays / styles / covers the ad itself. */}
                {status === 'loading' && (
                    <div
                        aria-hidden
                        className='absolute inset-0 pointer-events-none'
                        style={{ zIndex: 0 }}
                    >
                        <div
                            className='absolute inset-0'
                            style={{
                                background:
                                    'linear-gradient(110deg, rgba(127,0,255,0.06) 25%, rgba(255,138,255,0.15) 50%, rgba(127,0,255,0.06) 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'adsShimmer 1.6s linear infinite',
                            }}
                        />
                    </div>
                )}

                <ins
                    ref={insRef}
                    className='adsbygoogle'
                    style={{
                        display: 'block',
                        width: '100%',
                        minHeight,
                        position: 'relative',
                        zIndex: 1,
                        ...style,
                    }}
                    data-ad-client={client}
                    data-ad-slot={slot}
                    data-ad-format={format}
                    data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
                />

                <style>{`
                    @keyframes adsShimmer {
                        0% { background-position: -200% 0; }
                        100% { background-position: 200% 0; }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default ClientAdSense;
